import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["option", "children"],
  _excluded2 = ["option", "children"];
import { TypeaheadResults } from 'foundations-components/transitional/components/Typeahead';
import { SearchAutocompleteResultItem } from './SearchAutocompleteResultItem';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Fragment, useMemo } from 'react';
import { Text } from 'foundations-components/components/Text';
import ACInlineContentCard from 'academy-common-lib/components/card/ACInlineContentCard';
import { List } from 'foundations-components/transitional/components/List';
import FormattedList from 'I18n/components/FormattedList';
import FormattedDuration from 'I18n/components/FormattedDuration';
import { View } from 'foundations-components/components/View';
import { useLocation } from 'react-router-dom';
import { getOverviewModalParams } from 'academy-common-lib/utils/url';
import RouterLink from 'ui-addon-react-router-dom/RouterLink';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import { Button } from 'foundations-components/transitional/components/Button';
import { useTheme } from 'foundations-theming/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const OptionsTitle = ({
  children
}) => {
  return /*#__PURE__*/_jsx(Text, {
    paddingInline: "500",
    paddingBlockEnd: "200",
    paddingBlockStart: "500",
    textTransform: "uppercase",
    typography: "heading-50",
    children: children
  });
};
export const getPopularTermUrl = term => {
  return `/results?search=${encodeURIComponent(term)}`;
};
const PopularTermOptionItemComponent = _ref => {
  let {
      option
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  if (!(option !== null && option !== void 0 && option.text)) {
    return null;
  }
  return /*#__PURE__*/_jsx(RouterLink, Object.assign({}, rest, {
    to: getPopularTermUrl(option.text),
    use: "unstyled",
    children: /*#__PURE__*/_jsx(Button, {
      block: true,
      paddingBlock: "200",
      paddingInline: "500",
      use: "unstyled",
      children: /*#__PURE__*/_jsx(View, {
        inlineSize: "100%",
        textAlign: "start",
        children: option.text
      })
    })
  }));
};
export const makeInlineContentCardUrl = (option, location) => {
  const {
    resourceType,
    resourceId,
    language
  } = option;
  switch (resourceType) {
    case 'SHORT_FORM_CONTENT':
      {
        return `/shortvideo/${resourceId}`;
      }
    case 'LEARNING_PATH':
      {
        return `/saved-items/playlists/${resourceId}`;
      }
    default:
      {
        const searchParams = new URLSearchParams(location.search);
        const modalParams = getOverviewModalParams({
          id: resourceId,
          type: readUnhandledEnumCase(resourceType),
          language: readUnhandledEnumCase(language)
        });
        Object.entries(modalParams).forEach(([key, value]) => {
          searchParams.set(key, value);
        });
        return `${location.pathname}?${searchParams}`;
      }
  }
};
const QuickLinkOptionItemComponent = _ref2 => {
  let {
      option
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const location = useLocation();
  const to = makeInlineContentCardUrl(option, location);
  const {
    resourceDuration,
    resourceName,
    resourceThumbnail,
    resourceType,
    certificate,
    numberOfItems,
    numberOfLessons
  } = option;
  const makeResourceMetadata = () => {
    const metadata = [];
    if (numberOfItems) {
      metadata.push( /*#__PURE__*/_jsx(FormattedMessage, {
        "data-test-id": "playlist-total-items",
        message: "academy-navigation-lib.ACPage.header.search.autocompleteResults.inlineCard.resources.playlist.items",
        options: {
          count: numberOfItems
        }
      }, "playlist-total-items"));
    }
    if (numberOfLessons) {
      metadata.push( /*#__PURE__*/_jsx(FormattedMessage, {
        "data-test-id": "track-total-lessons",
        message: "academy-navigation-lib.ACPage.header.search.autocompleteResults.inlineCard.resources.track.lessons",
        options: {
          count: numberOfLessons
        }
      }, "track-total-lessons"));
    }
    if (resourceDuration) {
      metadata.push( /*#__PURE__*/_jsx(FormattedDuration, {
        format: "medium",
        from: +new Date() - resourceDuration * 1000
      }, "lesson-duration"));
    }
    return metadata;
  };
  const getResourceTypeMessage = () => {
    if (certificate) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "academy-navigation-lib.ACPage.header.search.autocompleteResults.inlineCard.resources.certificate"
      });
    }
    switch (resourceType) {
      case 'COURSE':
      case 'TRACK':
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academy-navigation-lib.ACPage.header.search.autocompleteResults.inlineCard.resources.type.track"
        });
      case 'LESSON':
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academy-navigation-lib.ACPage.header.search.autocompleteResults.inlineCard.resources.type.lesson"
        });
      case 'SHORT_FORM_CONTENT':
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academy-navigation-lib.ACPage.header.search.autocompleteResults.inlineCard.resources.type.shortFormContent"
        });
      case 'LEARNING_PATH':
      case 'PLAYLIST':
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academy-navigation-lib.ACPage.header.search.autocompleteResults.inlineCard.resources.type.learningPath"
        });
      default:
        return null;
    }
  };
  const theme = useTheme();
  return (
    /*#__PURE__*/
    // Raw div is needed to allow the rest props to be applied correctly - View doesn't pass down all the rest props
    _jsx("div", Object.assign({}, rest, {
      style: {
        paddingBlock: theme.space['200'],
        paddingInline: theme.space['500']
      },
      children: /*#__PURE__*/_jsx(ACInlineContentCard, {
        duration: resourceDuration,
        footer: /*#__PURE__*/_jsxs(List, {
          inline: true,
          use: "inline-divided",
          wrapChildren: true,
          children: [getResourceTypeMessage(), /*#__PURE__*/_jsx(FormattedList, {
            exclusive: true,
            children: makeResourceMetadata()
          })]
        }),
        name: resourceName,
        thumbnailSrc: resourceThumbnail,
        to: to,
        use: "no-hover"
      })
    }))
  );
};
const makeResults = suggestions => {
  let totalOptions = 0;
  return [{
    itemComponent: PopularTermOptionItemComponent,
    key: 'popularTerms',
    title: /*#__PURE__*/_jsx(OptionsTitle, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "academy-navigation-lib.ACPage.header.search.autocompleteResults.title.popularTerms"
      })
    }),
    options: suggestions.popularTerms.map(option => {
      return {
        text: option.term,
        value: option.term
      };
    })
  }, {
    itemComponent: QuickLinkOptionItemComponent,
    key: 'quickLinks',
    title: /*#__PURE__*/_jsx(OptionsTitle, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "academy-navigation-lib.ACPage.header.search.autocompleteResults.title.quickLinks"
      })
    }),
    options: suggestions.quickLinks.map(option => {
      return Object.assign({
        text: option.resourceName,
        value: option.resourceName
      }, option);
    })
  }].filter(item => item.options.length > 0).map(item => {
    const startIndex = totalOptions;
    totalOptions += item.options.length;
    return Object.assign({}, item, {
      startIndex
    });
  });
};
export const SearchAutocompleteResults = ({
  suggestions,
  highlightedOptionIndex,
  onResultItemMouseEnter,
  onResultItemClick
}) => {
  const results = useMemo(() => makeResults(suggestions), [suggestions]);
  if (!results.length) {
    return null;
  }
  return /*#__PURE__*/_jsx(TypeaheadResults, {
    maxBlockSize: "none",
    children: results.map(result => /*#__PURE__*/_jsxs(Fragment, {
      children: [result.title, result.options.map((resultItemOption, resultItemOptionIndex) => /*#__PURE__*/_jsx(SearchAutocompleteResultItem, {
        itemComponent: result.itemComponent,
        option: resultItemOption,
        index: resultItemOptionIndex + result.startIndex,
        highlightedOptionIndex: highlightedOptionIndex,
        onResultItemMouseEnter: (option, index) => {
          onResultItemMouseEnter(option, index, result);
        },
        onResultItemClick: (option, index) => {
          onResultItemClick(option, index, result);
        }
      }, `${result.key}-${resultItemOptionIndex}`))]
    }, result.key))
  });
};